/**
 * What the Molecule picker knows about one molecule, and the two pure
 * functions it is built on: how an option reads, and whether it matches the
 * filter (flowstack#1402).
 *
 * Deliberately free of `@/api` types. This directory holds props-only
 * components that are meant to move to flowstack-ui, so the caller maps its
 * wire rows onto {@link MoleculeOption} and nothing here reaches back into
 * the app's store or query layer.
 */

import { shortHash, truncateMiddle } from "@/lib/hash-display";

export interface MoleculeOption {
  /** The value the picker reports on change: the content hash. */
  id: string;
  /** The full content hash. Always shown, as secondary text, since it is the identity. */
  digest: string;
  /** Hill-notation formula, net charge as a suffix (`C7H5O2-`). */
  formula: string | null;
  atomCount: number | null;
  formalCharge: number | null;
  smiles: string | null;
  inchiKey: string | null;
  /** ISO-8601 time the molecule entered the catalog. */
  createdAt: string;
  /** Producing Tile, or null for a molecule a person uploaded. */
  producer: string | null;
  /** Producing run, or null for an upload. */
  runId: string | null;
}

/** `+1`, `-1`, `0` — ASCII minus, so a typed `-1` finds it. */
export function formatCharge(charge: number): string {
  return charge > 0 ? `+${charge}` : String(charge);
}

/**
 * Locale date and time, the same rendering the Artifacts page uses. `timeZone`
 * is an IANA zone; omitted, it is the browser's. An unparseable `iso` is
 * returned as is; a `timeZone` that is not a zone throws a `RangeError`.
 */
export function formatAdded(iso: string, timeZone?: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    timeZone,
  });
}

/**
 * `YYYY-MM-DD` in `timeZone` (default: the browser's), the zone
 * {@link formatAdded} shows. Not `iso.slice(0, 10)`: that is the UTC day, and
 * west of UTC a late-evening upload is shown on one day and would only be
 * found by typing the next.
 */
export function localIsoDate(iso: string, timeZone?: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso.slice(0, 10);
  const parts = new Intl.DateTimeFormat("en-US", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    timeZone,
  }).formatToParts(date);
  const part = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((p) => p.type === type)?.value ?? "";
  return `${part("year")}-${part("month")}-${part("day")}`;
}

/** Where the molecule came from: `uploaded`, or its Tile and (elided) run. */
export function describeOrigin(option: MoleculeOption): string {
  if (option.producer === null) return "uploaded";
  if (option.runId === null) return option.producer;
  return `${option.producer} · run ${truncateMiddle(option.runId, 18)}`;
}

/**
 * One line, because a native `<option>` holds text only. It reads from what a
 * person recognises to what identifies: chemistry first, then origin and date,
 * then the digest.
 */
export function moleculeOptionLabel(option: MoleculeOption): string {
  const chemistry = [
    option.formula ?? "formula unknown",
    option.atomCount === null ? null : `${option.atomCount} atoms`,
    option.formalCharge === null ? null : `charge ${formatCharge(option.formalCharge)}`,
  ]
    .filter((part): part is string => part !== null)
    .join(" · ");
  return `${chemistry} — ${describeOrigin(option)}, ${formatAdded(option.createdAt)} — ${shortHash(option.digest)}`;
}

/**
 * True when every whitespace-separated term of `query` occurs, ignoring case,
 * somewhere in the option: formula, SMILES, InChIKey, origin, run, digest, or
 * the date (as shown, and as `YYYY-MM-DD`, both in `timeZone`, default the
 * browser's). An empty query matches everything.
 */
export function matchesMoleculeFilter(
  option: MoleculeOption,
  query: string,
  timeZone?: string
): boolean {
  const terms = query.toLowerCase().split(/\s+/).filter(Boolean);
  if (terms.length === 0) return true;
  const haystack = [
    option.formula,
    option.smiles,
    option.inchiKey,
    option.producer ?? "uploaded",
    option.runId,
    option.digest,
    localIsoDate(option.createdAt, timeZone),
    formatAdded(option.createdAt, timeZone),
    option.atomCount === null ? null : `${option.atomCount} atoms`,
    option.formalCharge === null ? null : `charge ${formatCharge(option.formalCharge)}`,
  ]
    .filter((part): part is string => part !== null)
    .join("\n")
    .toLowerCase();
  return terms.every((term) => haystack.includes(term));
}
